import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ExistingAcademyAccountHelp from "@/components/auth/ExistingAcademyAccountHelp";
import { api, formatApiError } from "@/context/AuthContext";

const INTEREST_OPTIONS = [
  { value: "", label: "Skip — free theory only for now" },
  { value: "general_learner", label: "General learner driving" },
  { value: "manual_driving", label: "Manual driving" },
  { value: "overseas_driver", label: "Overseas-driver support" },
  { value: "older_driver_refresher", label: "Older-driver / refresher" },
  { value: "school_university", label: "School / university" },
];

export default function Register() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    email: "",
    display_name: "",
    password: "",
    password_confirm: "",
    accept_terms: false,
    training_interest: searchParams.get("interest") || "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [verifyLink, setVerifyLink] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [existingAccount, setExistingAccount] = useState(false);

  useEffect(() => {
    api
      .get("/auth/signup-status")
      .then((res) => setStatus(res.data))
      .catch(() =>
        setStatus({
          public_signup_open: false,
          message: "Could not check signup availability. Please try again shortly.",
        })
      );
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setExistingAccount(false);
    if (form.password !== form.password_confirm) {
      setError("Passwords do not match");
      return;
    }
    if (!form.accept_terms) {
      setError("Please accept the Privacy Notice and Terms to continue");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", {
        email: form.email.trim(),
        display_name: form.display_name.trim(),
        password: form.password,
        password_confirm: form.password_confirm,
        accept_terms: true,
        registration_source: "academy_public",
        ...(form.training_interest ? { training_interest: form.training_interest } : {}),
      });
      if (data.dev_verification_link) {
        setVerifyLink(data.dev_verification_link);
      }
      setSuccessMessage(data.message || "Account created. Please verify your email.");
    } catch (err) {
      const statusCode = err?.response?.status;
      const detail = formatApiError(err).toLowerCase();
      if (statusCode === 409 || detail.includes("already exists")) {
        setExistingAccount(true);
        setError("");
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  if (successMessage) {
    return (
      <div className="card max-w-xl mx-auto space-y-4" data-testid="register-success">
        <h1 className="text-base md:text-lg font-semibold">Check your email</h1>
        <p data-testid="register-success-message">{successMessage}</p>
        {verifyLink ? (
          <>
            <p className="text-sm text-muted-foreground">
              Development test mode is active (no production email delivery). Use the link below to
              verify this account.
            </p>
            <Link to={verifyLink} className="btn-primary inline-block" data-testid="dev-verification-link">
              Verify my email
            </Link>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            After verifying, return here to{" "}
            <Link to="/login" className="underline" data-testid="register-success-login">
              log in
            </Link>
            .
          </p>
        )}
      </div>
    );
  }

  if (status && status.public_signup_open === false) {
    return (
      <div className="card max-w-xl mx-auto space-y-4" data-testid="register-gated">
        <h1 className="text-base md:text-lg font-semibold">Learner signup is closed</h1>
        <p data-testid="register-gated-message">
          {status.message ||
            "Public learner signup is closed until GDI Academy V1 is live-certified."}
        </p>
        <p className="text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="underline" data-testid="register-login-link">
            Log in
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="card max-w-xl mx-auto space-y-6" data-testid="register-page">
      <div className="space-y-2">
        <h1 className="text-base md:text-lg font-semibold">Create your learner account</h1>
        <p className="text-sm text-muted-foreground">
          Public self-registration is for learners only. Instructor and organisation access remain
          invitation-controlled.
        </p>
      </div>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <div className="space-y-1">
          <label htmlFor="reg-name" className="font-medium">
            Display name
          </label>
          <input
            id="reg-name"
            data-testid="register-name-input"
            className="input"
            required
            maxLength={80}
            autoComplete="name"
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="reg-email" className="font-medium">
            Email
          </label>
          <input
            id="reg-email"
            data-testid="register-email-input"
            className="input"
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="reg-password" className="font-medium">
            Password (minimum 8 characters)
          </label>
          <div className="flex gap-2">
            <input
              id="reg-password"
              data-testid="register-password-input"
              className="input flex-1"
              type={showPassword ? "text" : "password"}
              required
              minLength={8}
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
            <button
              type="button"
              className="btn-outline"
              onClick={() => setShowPassword((v) => !v)}
              data-testid="register-password-toggle"
              aria-pressed={showPassword}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>
        <div className="space-y-1">
          <label htmlFor="reg-password-confirm" className="font-medium">
            Confirm password
          </label>
          <input
            id="reg-password-confirm"
            data-testid="register-password-confirm-input"
            className="input"
            type={showPassword ? "text" : "password"}
            required
            minLength={8}
            autoComplete="new-password"
            value={form.password_confirm}
            onChange={(e) => setForm({ ...form, password_confirm: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="reg-interest" className="font-medium">
            Training interest (optional)
          </label>
          <select
            id="reg-interest"
            data-testid="register-interest-select"
            className="input"
            value={form.training_interest}
            onChange={(e) => setForm({ ...form, training_interest: e.target.value })}
          >
            {INTEREST_OPTIONS.map((opt) => (
              <option key={opt.value || "skip"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            Free Road Code theory, practice quizzes and mock tests are available after email verification even if you
            skip this step.
          </p>
        </div>
        <div className="flex items-start gap-3">
          <input
            id="reg-terms"
            data-testid="register-terms-checkbox"
            type="checkbox"
            className="mt-1 h-5 w-5"
            checked={form.accept_terms}
            onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })}
            required
          />
          <label htmlFor="reg-terms" className="text-sm">
            I agree to the{" "}
            <Link to="/privacy" className="underline">
              Privacy Notice
            </Link>{" "}
            and{" "}
            <Link to="/terms" className="underline">
              Terms
            </Link>
            . I understand GDI Academy is an educational platform and is not NZTA.
          </label>
        </div>
        {existingAccount && (
          <ExistingAcademyAccountHelp email={form.email.trim()} testIdPrefix="register-existing" />
        )}
        {error && (
          <p role="alert" className="text-destructive" data-testid="register-error">
            {error}
          </p>
        )}
        <button type="submit" className="btn-primary w-full sm:w-auto" disabled={busy || status === null} data-testid="register-submit-button">
          {busy ? "Creating your account…" : "Create Learner Account"}
        </button>
      </form>
      <p className="text-sm text-muted-foreground">
        Already have an account?{" "}
        <Link to="/login" className="underline" data-testid="register-login-link">
          Log in
        </Link>
      </p>
    </div>
  );
}
